import React, {
    createContext,
    useCallback,
    useEffect,
    useReducer,
    useRef,
} from 'react'
import { tokenManager } from '../services/tokenManager'
import { authService } from '../services/authService'
import { apiClient, AUTH_EXPIRED_MESSAGE_KEY } from '../services/apiClient'
import { tabSync } from '../services/tabSync'
import type {
    AuthUser,
    AuthContextValue,
    LoginCredentials,
    AuthResponse,
    AuthState,
} from '../types'
import { useDispatch } from 'react-redux'
import {
    setInitData,
    setLoggedInUser,
} from '../../features/security/security.slice'

const INTENDED_URL_KEY = 'sfy_cms_intended_url'

export const saveIntendedUrl = (url: string): void => {
    if (url && url !== '/login' && url !== '/unauthorized') {
        sessionStorage.setItem(INTENDED_URL_KEY, url)
    }
}

export const getAndClearIntendedUrl = (): string => {
    const url = sessionStorage.getItem(INTENDED_URL_KEY)
    sessionStorage.removeItem(INTENDED_URL_KEY)
    return url || '/'
}

type AuthAction =
    | { type: 'LOGIN_SUCCESS'; user: AuthUser; expiresAt: number }
    | { type: 'LOGOUT' }
    | { type: 'SESSION_EXPIRED' }
    | { type: 'SET_LOADING'; isLoading: boolean }
    | { type: 'DISMISS_SESSION_EXPIRED' }
    | { type: 'UPDATE_EXPIRY'; expiresAt: number }
    | { type: 'SET_APP_INITIATED'; initiated: boolean }

const initialAuthState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isSessionExpired: false,
    sessionExpiresAt: 0,
    isAppInitiated: true,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
    switch (action.type) {
        case 'LOGIN_SUCCESS':
            return {
                ...state,
                user: action.user,
                isAuthenticated: true,
                isLoading: false,
                isSessionExpired: false,
                sessionExpiresAt: action.expiresAt,
            }
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isSessionExpired: false,
                sessionExpiresAt: 0,
            }
        case 'SESSION_EXPIRED':
            return {
                ...state,
                isSessionExpired: true,
                sessionExpiresAt: 0,
            }
        case 'SET_LOADING':
            return { ...state, isLoading: action.isLoading }
        case 'DISMISS_SESSION_EXPIRED':
            return {
                ...state,
                isSessionExpired: false,
                user: null,
            }
        case 'UPDATE_EXPIRY':
            return { ...state, sessionExpiresAt: action.expiresAt }
        case 'SET_APP_INITIATED':
            return { ...state, isAppInitiated: action.initiated }
        default:
            return state
    }
}

export const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
    children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialAuthState)
    const reduxDispatch = useDispatch()
    const mountedRef = useRef(false)
    const stateRef = useRef(state)
    stateRef.current = state

    const loggingOutRef = useRef(false)

    const handleAuthFailure = useCallback(() => {
        if (loggingOutRef.current) return
        if (!stateRef.current.user) return

        tokenManager.clear()
        sessionStorage.setItem(
            AUTH_EXPIRED_MESSAGE_KEY,
            'Your session has expired. Please log in again.'
        )
        dispatch({ type: 'LOGOUT' })
        tabSync.broadcast({ type: 'AUTH_LOGOUT' })
    }, [])

    useEffect(() => {
        apiClient.setOnAuthFailure(handleAuthFailure)
    }, [handleAuthFailure])

    const performRefresh = useCallback(async (): Promise<string | null> => {
        try {
            const result = await authService.refresh()
            if (result) {
                dispatch({
                    type: 'LOGIN_SUCCESS',
                    user: result.user,
                    expiresAt: tokenManager.getExpiresAt(),
                })
                reduxDispatch(setInitData(result.initData))
                reduxDispatch(setLoggedInUser(result.user))
                tabSync.broadcast({
                    type: 'AUTH_SESSION_EXTENDED',
                    token: result.token,
                    user: result.user,
                    initData: result.initData,
                    expiresIn: result.expires_in,
                })
                return result.token
            }
        } catch {
            // Refresh failed — token expired or invalid
        }
        return null
    }, [reduxDispatch])

    useEffect(() => {
        tokenManager.setRefreshCallback(performRefresh)
        apiClient.setRefreshTokenFn(performRefresh)
    }, [performRefresh])

    useEffect(() => {
        if (mountedRef.current) return
        mountedRef.current = true

        const initialize = async () => {
            try {
                const result = await authService.refresh()
                if (result) {
                    dispatch({
                        type: 'LOGIN_SUCCESS',
                        user: result.user,
                        expiresAt: tokenManager.getExpiresAt(),
                    })
                    reduxDispatch(setInitData(result.initData))
                    reduxDispatch(setLoggedInUser(result.user))
                } else {
                    dispatch({ type: 'SET_LOADING', isLoading: false })
                }
            } catch (err: unknown) {
                const axiosErr = err as {
                    response?: { data?: { initiated?: boolean } }
                }
                if (axiosErr?.response?.data?.initiated === false) {
                    dispatch({ type: 'SET_APP_INITIATED', initiated: false })
                }
                dispatch({ type: 'SET_LOADING', isLoading: false })
            } finally {
                document.body.classList.remove('with-main-preloader')
            }
        }

        initialize()
    }, [reduxDispatch])

    useEffect(() => {
        tabSync.initialize()

        const unsubscribe = tabSync.subscribe((event) => {
            switch (event.type) {
                case 'AUTH_LOGIN':
                case 'AUTH_SESSION_EXTENDED':
                    tokenManager.setToken(event.token, event.expiresIn)
                    dispatch({
                        type: 'LOGIN_SUCCESS',
                        user: event.user,
                        expiresAt: tokenManager.getExpiresAt(),
                    })
                    reduxDispatch(setInitData(event.initData))
                    reduxDispatch(setLoggedInUser(event.user))
                    break
                case 'AUTH_LOGOUT':
                    loggingOutRef.current = true
                    sessionStorage.removeItem(AUTH_EXPIRED_MESSAGE_KEY)
                    tokenManager.clear()
                    dispatch({ type: 'LOGOUT' })
                    loggingOutRef.current = false
                    break
                case 'AUTH_SESSION_EXPIRED':
                    dispatch({ type: 'SESSION_EXPIRED' })
                    break
            }
        })

        return () => {
            unsubscribe()
        }
    }, [reduxDispatch])

    const login = useCallback(
        async (credentials: LoginCredentials): Promise<AuthResponse> => {
            const response = await authService.login(credentials)

            if (response.success) {
                const { user, initData, token, expires_in } = response.data
                dispatch({
                    type: 'LOGIN_SUCCESS',
                    user,
                    expiresAt: tokenManager.getExpiresAt(),
                })
                reduxDispatch(setInitData(initData))
                reduxDispatch(setLoggedInUser(user))
                tabSync.broadcast({
                    type: 'AUTH_LOGIN',
                    token,
                    user,
                    initData,
                    expiresIn: expires_in,
                })
            }

            return response
        },
        [reduxDispatch]
    )

    const logout = useCallback(async (): Promise<void> => {
        loggingOutRef.current = true
        try {
            await authService.logout()
        } finally {
            sessionStorage.removeItem(AUTH_EXPIRED_MESSAGE_KEY)
            dispatch({ type: 'LOGOUT' })
            tabSync.broadcast({ type: 'AUTH_LOGOUT' })
            loggingOutRef.current = false
        }
    }, [])

    const reAuthenticate = useCallback(
        async (password: string): Promise<boolean> => {
            const currentUser = stateRef.current.user
            if (!currentUser) return false
            try {
                const response = await authService.reAuthenticate(
                    currentUser.username,
                    password
                )
                if (response.success) {
                    const { user, initData, token, expires_in } = response.data
                    dispatch({
                        type: 'LOGIN_SUCCESS',
                        user,
                        expiresAt: tokenManager.getExpiresAt(),
                    })
                    reduxDispatch(setInitData(initData))
                    reduxDispatch(setLoggedInUser(user))
                    tabSync.broadcast({
                        type: 'AUTH_SESSION_EXTENDED',
                        token,
                        user,
                        initData,
                        expiresIn: expires_in,
                    })
                    return true
                }
                return false
            } catch {
                return false
            }
        },
        [reduxDispatch]
    )

    const extendSession = useCallback(async (): Promise<void> => {
        await performRefresh()
    }, [performRefresh])

    const dismissSessionExpired = useCallback(() => {
        dispatch({ type: 'DISMISS_SESSION_EXPIRED' })
    }, [])

    const setAppInitiated = useCallback((initiated: boolean) => {
        dispatch({ type: 'SET_APP_INITIATED', initiated })
    }, [])

    const contextValue: AuthContextValue = {
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        isSessionExpired: state.isSessionExpired,
        sessionExpiresAt: state.sessionExpiresAt,
        isAppInitiated: state.isAppInitiated,
        setAppInitiated,
        login,
        logout,
        reAuthenticate,
        extendSession,
        dismissSessionExpired,
    }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}
